import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

// We import the types from the parent container or redefine them if they aren't exported.
// For now, we'll redefine the necessary props.
interface DesktopAlphaFusionProps {
    data: any;
    activeTab: 'opportunities' | 'sell' | 'universe' | 'info';
    setActiveTab: (tab: 'opportunities' | 'sell' | 'universe' | 'info') => void;
    filters: any;
    setFilters: (filters: any) => void;
    opportunities: any[];
    sellSignals: any[];
    filteredUniverse: any[];
    sentiment: string;
    sentimentColors: Record<string, { bg: string; color: string }>;
    navigate: (path: string) => void;
}

const DesktopAlphaFusion: React.FC<DesktopAlphaFusionProps> = ({
    data,
    activeTab,
    setActiveTab,
    filters,
    setFilters,
    opportunities,
    sellSignals,
    filteredUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <Disclaimer type="investment" />

            {/* Summary */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>🎯 Configurations Alpha</span>
                        <span style={styles.summaryValue}>{opportunities.length}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📈 Sentiment</span>
                        <span style={{
                            ...styles.badge,
                            backgroundColor: sentimentColors[sentiment].bg,
                            color: sentimentColors[sentiment].color,
                        }}>
                            {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                        </span>
                    </div>
                </div>
            </Box>

            {/* Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Configurations Haussières ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Alertes Baissières ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers ({filteredUniverse.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Stratégie
                </button>
            </div>

            {/* Opportunities */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p>Aucune action en 'Alpha Mode' (NVI + Guppy) actuellement.</p>
                                <p style={{ fontSize: 14 }}>Patience, la Smart Money accumule.</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${opp.symbole}`)}>
                                                {opp.symbole}
                                            </span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: opp.score >= 150 ? '#ec4899' : '#7c3aed' }}>
                                                {opp.score}
                                            </div>
                                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Score</span>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div>
                                            <span style={styles.priceLabel}>Prix</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString('fr-FR')}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>NVI</span>
                                            <span style={{ ...styles.priceValue, color: opp.nvi_bullish ? '#16a34a' : '#9ca3af' }}>
                                                {opp.nvi_bullish ? 'Strong' : 'Weak'}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Guppy</span>
                                            <span style={{ ...styles.priceValue, color: opp.guppy_bullish ? '#4ade80' : '#9ca3af' }}>
                                                {opp.guppy_bullish ? 'Bull' : 'Bear'}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>TRIX</span>
                                            <span style={{ ...styles.priceValue, color: '#2563eb' }}>
                                                {opp.trix?.toFixed(1)}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {opp.is_bull_flag && (
                                            <span style={{ fontSize: 12, backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                                🏳️ Bull Flag Breakout
                                            </span>
                                        )}
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p>Pas d'alerte de retournement. Vos positions sont en tendance.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal: any) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${signal.symbole}`)}>
                                                {signal.symbole}
                                            </span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 'bold' }}>ALERTE BAISSIÈRE</span>
                                        </div>
                                    </div>
                                    <div style={styles.priceGrid}>
                                        <div>
                                            <span style={styles.priceLabel}>Prix</span>
                                            <span style={styles.priceValue}>{signal.current_price?.toLocaleString('fr-FR')}</span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>P&L</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#4ade80' : '#f87171' }}>
                                                {signal.unrealized_pnl_pct >= 0 ? '+' : ''}{signal.unrealized_pnl_pct?.toFixed(2)}%
                                            </span>
                                        </div>
                                        <div>
                                            <span style={styles.priceLabel}>Qté</span>
                                            <span style={styles.priceValue}>{signal.quantity_held}</span>
                                        </div>
                                    </div>
                                    <p style={styles.justification}>{signal.justification}</p>
                                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                                        EMA30 ({signal.ema_30?.toFixed(0)}) {'<'} EMA60 ({signal.ema_60?.toFixed(0)})
                                    </div>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Universe */}
            {activeTab === 'universe' && (
                <Box>
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-box)', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                            <input
                                type="text"
                                placeholder="🔍 Rechercher..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                style={styles.searchInput}
                            />
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button
                                    onClick={() => setFilters({ ...filters, nviOnly: !filters.nviOnly })}
                                    style={{ ...styles.filterChip, ...(filters.nviOnly ? styles.filterChipActive : {}) }}
                                >
                                    🟢 NVI Smart Money
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, guppyOnly: !filters.guppyOnly })}
                                    style={{ ...styles.filterChip, ...(filters.guppyOnly ? styles.filterChipActive : {}) }}
                                >
                                    🔵 Guppy Trend
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, bullFlagOnly: !filters.bullFlagOnly })}
                                    style={{ ...styles.filterChip, ...(filters.bullFlagOnly ? styles.filterChipActive : {}) }}
                                >
                                    🏳️ Bull Flag
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, trixPositive: !filters.trixPositive })}
                                    style={{ ...styles.filterChip, ...(filters.trixPositive ? styles.filterChipActive : {}) }}
                                >
                                    📈 TRIX {'>'} 0
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, highScore: !filters.highScore })}
                                    style={{ ...styles.filterChip, ...(filters.highScore ? styles.filterChipActive : {}) }}
                                >
                                    ✨ Score {'>'} 150
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, undervaluedOnly: !filters.undervaluedOnly })}
                                    style={{ ...styles.filterChip, ...(filters.undervaluedOnly ? styles.filterChipActive : {}) }}
                                >
                                    💎 Sous-évaluée
                                </button>
                            </div>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.universeTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Symbole</th>
                                    <th style={styles.th}>Prix</th>
                                    <th style={styles.thCenter}>Score</th>
                                    <th style={styles.thCenter}>NVI (Smart)</th>
                                    <th style={styles.thCenter}>Guppy (Trend)</th>
                                    <th style={styles.thCenter}>Flag</th>
                                    <th style={styles.thCenter}>TRIX</th>
                                    <th style={styles.thCenter}>Décote</th>
                                    <th style={styles.thCenter}>Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUniverse.map((stock: any) => (
                                    <tr key={stock.symbole} style={{
                                        backgroundColor: stock.is_opportunity ? 'rgba(234, 179, 8, 0.1)' : stock.is_owned ? 'rgba(56, 189, 248, 0.1)' : 'transparent'
                                    }}>
                                        <td style={styles.td}>
                                            <span style={styles.symbolLink} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                                {stock.symbole}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{stock.cours_actuel?.toLocaleString('fr-FR')}</td>
                                        <td style={{ ...styles.tdCenter, fontWeight: 'bold' }}>{stock.score}</td>
                                        <td style={styles.tdCenter}>{stock.nvi_bullish ? '🟢' : '⚪'}</td>
                                        <td style={styles.tdCenter}>{stock.guppy_bullish ? '🟢' : '🔴'}</td>
                                        <td style={styles.tdCenter}>{stock.is_bull_flag ? '🏳️' : '-'}</td>
                                        <td style={{ ...styles.tdCenter, color: '#2563eb' }}>{stock.trix?.toFixed(1)}</td>
                                        <td style={{ ...styles.tdCenter, color: stock.valuation_upside > 0 ? '#4ade80' : '#f87171', fontWeight: stock.valuation_upside > 10 ? 'bold' : 'normal' }}>
                                            {stock.valuation_upside ? `${stock.valuation_upside > 0 ? '+' : ''}${stock.valuation_upside.toFixed(0)}%` : '-'}
                                        </td>
                                        <td style={styles.tdCenter}>
                                            {stock.status === 'ALPHA_MODE' ?
                                                <span style={{ color: '#4ade80', fontWeight: 'bold' }}>TITAN 🚀</span> :
                                                stock.status === 'BULLISH_TREND' ? 'En Hausse' : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>
            )}

            {/* Strategy Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 20 }}>
                        <h3>🚀 Stratégie : Indicateurs de momentum</h3>
                        <p>Une méthode basée sur des indicateurs de momentum (flux de niveau institutionnel, structure de continuation et tendance de fond).</p>

                        <h4 style={{ marginTop: 20 }}>1. Smart Money (NVI)</h4>
                        <p>Le <strong>Negative Volume Index (NVI)</strong> détecte ce que font les "Mains Fortes" (Institutionnels) quand le public se désintéresse (faible volume). <br />
                            Si <strong>NVI {'>'} Moyenne(255)</strong>, alors les pros accumulent en silence.</p>

                        <h4 style={{ marginTop: 20 }}>2. Tendance Lourde (Guppy)</h4>
                        <p>On n'achète que si la tendance de fond est haussière.<br />
                            Signal : <strong>EMA 30 {'>'} EMA 60</strong>. Cela évite les faux départs.</p>

                        <h4 style={{ marginTop: 20 }}>3. Priorisation (Bull Flag)</h4>
                        <p>Si plusieurs signaux apparaissent, on privilégie ceux qui cassent une figure de consolidation (Drapeau) immédiatement.</p>

                        <h4 style={{ marginTop: 20 }}>🔴 Sortie (Exit)</h4>
                        <p>On sort uniquement quand la <strong>Tendance Lourde casse</strong> (EMA 30 passe sous EMA 60). On ne sort pas sur une petite baisse de panique.</p>
                    </div>
                </Box>
            )}

        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, padding: 20 },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 },
    summaryValue: { fontSize: 20, fontWeight: 'bold', color: 'var(--text-heading)' },
    badge: { padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 8, marginBottom: 20, marginTop: 20 },
    tab: { flex: 1, padding: '12px 16px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)', borderBottom: '2px solid transparent' },
    tabActive: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#2563eb', color: 'white' },
    tabActiveInfo: { backgroundColor: '#0ea5e9', color: 'white' },
    cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 16 },
    card: { padding: 16, backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)', height: '100%' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
    symbolLink: { fontSize: 18, fontWeight: 'bold', color: '#60a5fa', cursor: 'pointer' },
    companyName: { fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0 0' },
    scoreBadge: { width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: 16 },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 12, marginBottom: 12, padding: 12, backgroundColor: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--border-color)' },
    priceLabel: { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 },
    priceValue: { display: 'block', fontSize: 14, fontWeight: '600', color: 'var(--text-heading)' },
    positionInfo: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-main)', marginBottom: 12, padding: '8px 0', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' },
    justification: { fontSize: 13, color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' },
    universeTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    thCenter: { textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    td: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'middle', color: 'var(--text-main)' },
    tdCenter: { padding: '10px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'center', color: 'var(--text-main)' },
    refreshBtn: { marginTop: 16, padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' },
    searchInput: { width: '200px', padding: '8px 12px', borderRadius: 20, border: '1px solid var(--border-color)', fontSize: 13, outline: 'none', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)' },
    filterChip: { padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-box)', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', color: 'var(--text-muted)' },
    filterChipActive: { backgroundColor: 'rgba(56, 189, 248, 0.1)', borderColor: '#38bdf8', color: '#38bdf8', fontWeight: 'bold' },
};

export default DesktopAlphaFusion;
